import type {
  ToolPermission,
  ToolPermissionsView,
} from "../types/integration";

// The "overlay rules onto the catalog tool list" derivation for the Available
// tools section, factored out of IntegrationDetail so it's unit-testable without
// a DOM (no jsdom in this repo). The lean ToolPermissionsView carries ONLY the
// integration-level default plus the tenant's explicit per-tool rules: presence
// in `rules` IS the explicit flag, so a tool with no rule simply follows the
// default. Kept pure: build the overlay once, then resolve each catalog row.

export type ToolPermissionEntry = {
  permission: ToolPermission;
  explicit: true;
};

export type PermissionOverlay = {
  defaultPermission: ToolPermission;
  byTool: ReadonlyMap<string, ToolPermissionEntry>;
};

export const buildPermissionOverlay = (
  view: ToolPermissionsView,
): PermissionOverlay => ({
  defaultPermission: view.defaultPermission,
  byTool: new Map(
    view.rules.map((r) => [r.toolSlug.toUpperCase(), { permission: r.permission, explicit: true }]),
  ),
});

// Per-row resolution: an explicit rule wins; otherwise the row follows the
// integration default and is not explicit (rendered as "following default", no
// reset affordance). A rule whose toolSlug isn't a catalog row is never looked
// up here, so it's silently absent from the rendered list.
export const effectiveToolPermission = (
  overlay: PermissionOverlay,
  toolSlug: string,
): { permission: ToolPermission; explicit: boolean } => {
  const rule = overlay.byTool.get(toolSlug.toUpperCase());
  return {
    permission: rule?.permission ?? overlay.defaultPermission,
    explicit: rule?.explicit ?? false,
  };
};
